import React, { useEffect, useId, useRef } from 'react';
import { Gradient } from 'whatamesh';
import './GradientCanvas.css';

/**
 * Authentic Stripe-style mesh gradient via the minigl "whatamesh" Gradient class
 * (the same implementation as the reference CodePen). Colors are supplied through
 * the --gradient-color-* CSS variables the library reads off the canvas.
 *
 * The library assumes a full-viewport-width canvas, so use this inside a
 * full-bleed (100vw) container for correct sizing.
 */
const GradientCanvas = ({
  colors = ['#2b1d8f', '#4b46ff', '#ff45c8', '#35d6ff'],
  amp,
  darkenTop = false,
  className = '',
}) => {
  const rawId = useId();
  const canvasId = `grad-${rawId.replace(/[^a-zA-Z0-9]/g, '')}`;

  useEffect(() => {
    let gradient;
    let cancelled = false;

    // Defer to next frame so the canvas has a measured size before init.
    const raf = requestAnimationFrame(() => {
      if (cancelled) return;
      try {
        gradient = new Gradient();
        if (amp != null) gradient.amp = amp;
        gradient.initGradient(`#${canvasId}`);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('GradientCanvas init failed:', err);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      try {
        if (gradient && typeof gradient.disconnect === 'function') {
          gradient.disconnect();
        }
      } catch (_) {
        /* noop */
      }
    };
  }, [canvasId, amp]);

  const style = {
    '--gradient-color-1': colors[0],
    '--gradient-color-2': colors[1],
    '--gradient-color-3': colors[2],
    '--gradient-color-4': colors[3],
  };

  return (
    <canvas
      id={canvasId}
      className={`gradientCanvas ${className}`.trim()}
      style={style}
      data-transition-in=""
      {...(darkenTop ? { 'data-js-darken-top': '' } : {})}
      aria-hidden="true"
    />
  );
};

export default GradientCanvas;
